/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import { BrickLoader, UniversalWrap } from "~/components";
import {
  CostTableData,
  getColorsPalette,
  getTableColumns,
  StackBarChart,
  Wrapper,
} from "~/components/Charts/CostAnalysis";
import { tooltipStyles } from "~/components/Charts/CostAnalysis/components";
import {
  generateLinkParameters,
  getEndDate,
  mapStackBarChartColors,
} from "~/components/Charts/CostAnalysis/helpers";
import {
  ChartType,
  Granularity,
  LinkQueryParameterName,
  Period,
} from "~/components/Charts/CostAnalysis/types";
import { useCurrency } from "~/hooks";
import { useOrgUnitCostPerApplicationChart } from "../hooks";

type Props = {
  id: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function OrgUnitCostPerApplicationView({
  id,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();

  const {
    costPerApplicationChartData,
    costPerApplicationTableData,
    costPerApplicationTotal,
    loading,
    error,
    applications,
  } = useOrgUnitCostPerApplicationChart({
    orgUnitId: id,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerApplicationTableData,
        hasFooter,
        period,
        chartType: ChartType.COST_PER_APPLICATION,
        colorsPalette: getColorsPalette(ChartType.COST_PER_APPLICATION),
        linkParameters: generateLinkParameters(
          LinkQueryParameterName.APPLICATION,
          []
        ),
      }),
    [startDate, endDate, currency, hasFooter, costPerApplicationTableData]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading: false, Component: BrickLoader }}
      errorProps={{ error: undefined }}
    >
      <Wrapper onMouseLeave={hideTooltip}>
        <ParentSize>
          {(parent) => (
            <>
              <StackBarChart
                width={parent.width}
                height={280}
                data={costPerApplicationChartData}
                granularity={granularity}
                loading={loading}
                startDate={startDate}
                endDate={endDate}
                period={period}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                colors={mapStackBarChartColors(applications)}
                keys={applications}
                containerRef={containerRef}
              />
              <When condition={tooltipOpen}>
                <TooltipInPortal
                  // set key to random so it correctly updates with parent bounds
                  key={Date.now()}
                  top={tooltipTop}
                  left={tooltipLeft}
                  style={tooltipStyles}
                >
                  {tooltipData}
                </TooltipInPortal>
              </When>
            </>
          )}
        </ParentSize>
      </Wrapper>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={costPerApplicationTableData}
        chartType={ChartType.COST_PER_APPLICATION}
        total={costPerApplicationTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
        linkParameters={generateLinkParameters(
          LinkQueryParameterName.APPLICATION,
          []
        )}
      />
    </UniversalWrap>
  );
}
